import React, { useCallback, useMemo, useContext } from 'react';
import { Select, InputLabel, MenuItem, FormControl } from '@material-ui/core';

import { useId } from '../../../../hooks';
import { selectStyles } from './styles';
import { ModalComponentContext } from './ModalComponent';

type Props = {
  label: string;
  items: Array<{ value: number; title: React.ReactNode }>;
  value: number;
  changeHandler: (value: number) => void;
  noneValue?: string | number;
  isNone?: boolean;
  disabled?: boolean;
};

/* eslint-disable no-param-reassign, react-hooks/exhaustive-deps */

const SelectComponent = ({
  label, items, value, changeHandler, noneValue = -1, isNone = true, disabled = false,
}: Props) => {
  const classes = selectStyles();
  const selectId = useId();
  const { modalContentRef } = useContext(ModalComponentContext);

  const onChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    (async () => changeHandler(Number(event.target.value)))();
  }, [changeHandler]);

  const selectStyle = useMemo(() => {
    const modalContent = modalContentRef.current;

    if (modalContent) {
      return {
        maxWidth: `${modalContent.offsetWidth * 0.8}px`,
        maxHeight: `${modalContent.offsetHeight * 0.8}px`,
      };
    }

    return {
      maxWidth: '80%',
      maxHeight: '50%',
    };
  }, [modalContentRef.current]);

  const onResizeSelect = useCallback((select: HTMLElement) => {
    const modalContent = modalContentRef.current;
    if (modalContent) {
      const modalCoords = modalContent.getBoundingClientRect();
      const selectCoords = select.getBoundingClientRect();
      if (selectCoords.bottom > modalCoords.bottom) {
        const maxHeight = selectCoords.bottom - modalCoords.bottom;
        if (maxHeight < 140) {
          const top = selectCoords.top - maxHeight;
          select.style.top = `${top}px`;
        } else {
          select.style.maxHeight = `${maxHeight}px`;
        }
      }
      select.style.opacity = '1';
    }
  }, [modalContentRef.current]);

  return (
    <FormControl disabled={disabled} style={{ width: '100%' }}>
      <InputLabel
        htmlFor={selectId}
        className={classes.label}
        classes={{
          root: classes.label,
          focused: classes.focused,
        }}
      >
        {label}
      </InputLabel>
      <Select
        id={selectId}
        value={value}
        onChange={onChange}
        className={classes.root}
        MenuProps={{
          keepMounted: false,
          style: {
            zIndex: 2147483100,
          },
          TransitionProps: {
            style: selectStyle,
          },
          onEntered: onResizeSelect,
          onEntering: (select: HTMLElement) => {
            select.style.opacity = '0';
          },
        }}
      >
        {isNone
        && (
          <MenuItem value={noneValue}>
            <em>None</em>
          </MenuItem>
        )}
        {items.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectComponent;
